import React, { useState, useEffect } from 'react';
import { getJiraTickets, removeJiraTicket } from '../../../api/integrations';

const JiraTicketsPanel = ({ testCaseId, onCreateTicket, onTicketRemoved }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTimestamp, setRefreshTimestamp] = useState(Date.now());

  useEffect(() => {
    if (testCaseId) {
      fetchJiraTickets();
    }
    
    const intervalId = setInterval(() => {
      if (testCaseId) {
        if (!loading) {
          fetchJiraTickets(true);
        }
      }
    }, 5000);
    
    return () => clearInterval(intervalId);
  }, [testCaseId, refreshTimestamp]);

  const fetchJiraTickets = async (silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    
    try {
      const response = await getJiraTickets(testCaseId);
      
      let ticketsData = [];
      
      if (Array.isArray(response)) {
        ticketsData = response;
      } else if (response.tickets && Array.isArray(response.tickets)) {
        ticketsData = response.tickets;
      } else if (response.jira_tickets && Array.isArray(response.jira_tickets)) {
        ticketsData = response.jira_tickets;
      }
      
      if (JSON.stringify(ticketsData) !== JSON.stringify(tickets)) {
        // Log the tickets data to help debug
        console.log('Jira tickets fetched:', ticketsData);
        
        // Process each ticket to ensure it has a summary field
        const processedTickets = ticketsData.map(ticket => {
          // Make sure each ticket has a summary field
          if (!ticket.summary) {
            return {
              ...ticket,
              summary: ticket.title || `Jira Issue: ${ticket.issue_key || ticket.key || 'Unknown'}`
            };
          }
          return ticket;
        });
        
        setTickets(processedTickets);
        console.log('Jira tickets updated:', processedTickets.length);
      }
    } catch (err) {
      console.error("Error fetching Jira tickets:", err);
      if (!silent) {
        setTickets([]);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const handleRemoveTicket = async (ticketId) => {
    if (!window.confirm("Are you sure you want to remove this Jira ticket association?")) {
      return;
    }

    setLoading(true);
    try {
      await removeJiraTicket(testCaseId, ticketId);
      setTickets(tickets.filter(ticket => ticket.id !== ticketId));
      if (onTicketRemoved) onTicketRemoved();
      
      setRefreshTimestamp(Date.now());
    } catch (err) {
      // Silently handle error
    } finally {
      setLoading(false);
    }
  };
  
  const refreshTickets = () => {
    setRefreshTimestamp(Date.now());
  };

  const handleCreateTicket = () => {
    if (onCreateTicket) {
      onCreateTicket();
      
      setTimeout(() => {
        refreshTickets();
      }, 1000);
    }
  };

  if (loading && tickets.length === 0) {
    return (
      <div className="py-4 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800 mx-auto"></div>
        <p className="text-sm text-gray-600 mt-2">Loading Jira tickets...</p>
      </div>
    );
  }

  return (
    <div className="py-2 overflow-visible">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <svg className="h-5 w-5 text-blue-600 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" title="Jira tickets">
            <path d="M12 2L4 7L12 12L20 7L12 2Z" fill="#2684FF"/>
            <path d="M4 7V17L12 22L20 17V7" stroke="#2684FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Jira Tickets
        </h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={refreshTickets}
            className="px-2 py-1 bg-gray-100 rounded-md hover:bg-gray-200 flex items-center text-sm text-gray-700"
          >
            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
          <button
            onClick={handleCreateTicket}
            className="px-2 py-1 bg-blue-600 rounded-md hover:bg-blue-700 flex items-center text-sm text-white"
          >
            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Create Ticket
          </button>
        </div>
      </div>

      {tickets.length === 0 ? (
        <div className="text-center py-6 bg-gray-50 rounded-md">
          <svg 
            className="mx-auto h-12 w-12 text-gray-400" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor" 
            aria-hidden="true"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="2" 
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Jira tickets</h3>
          <p className="mt-1 text-sm text-gray-500">
            There are no Jira tickets associated with this test case.
          </p>
          <div className="mt-4">
            <button
              type="button"
              onClick={handleCreateTicket}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              title="Create a new Jira ticket for this test case"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create a ticket
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3 overflow-y-auto max-h-[calc(100vh-280px)]">
          {tickets.map((ticket) => (
            <div key={ticket.id || `${ticket.issue_key}-${Date.now()}`} className="border rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between px-4 py-3 bg-blue-50 border-b">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-blue-600 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L4 7L12 12L20 7L12 2Z" fill="#2684FF"/>
                    <path d="M4 7V17L12 22L20 17V7" stroke="#2684FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="font-medium text-blue-700">
                    {ticket.issue_key || ticket.key} 
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <a
                    href={ticket.issue_url || ticket.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center bg-white border border-blue-300 rounded-md px-2 py-1 transition-colors hover:bg-blue-100"
                    title="Open this ticket in Jira"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    View in Jira
                  </a>
                  <button
                    onClick={() => handleRemoveTicket(ticket.id || ticket.ticket_id)}
                    className="text-sm text-red-600 hover:text-red-800 flex items-center bg-white border border-red-200 rounded-md px-2 py-1 transition-colors hover:bg-red-50"
                    title="Remove association with this test case"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Remove
                  </button>
                </div>
              </div>
              <div className="px-4 py-3">
                <h4 className="font-medium text-gray-900 text-lg mb-2 break-words">
                  {ticket.summary || `Jira Issue: ${ticket.issue_key || ticket.key}`}
                </h4>
                
                {ticket.description && (
                  <div className="mb-3 text-sm text-gray-600 border-l-4 border-blue-200 pl-3 py-1 bg-blue-50 rounded-r-md">
                    {ticket.description.length > 150 
                      ? `${ticket.description.substring(0, 150)}...` 
                      : ticket.description}
                  </div>
                )}
                
                <div className="flex flex-wrap gap-2 items-center text-sm">
                  {(ticket.status || ticket.issue_status) && (
                    <span className="px-2 py-1 rounded-full bg-green-100 text-green-800 flex items-center">
                      <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {ticket.status || ticket.issue_status}
                    </span>
                  )}
                  
                  {(ticket.issue_type || ticket.type) && (
                    <span className="px-2 py-1 rounded-full bg-purple-100 text-purple-800 flex items-center">
                      <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      {ticket.issue_type || ticket.type}
                    </span>
                  )}
                  
                  {(ticket.priority || ticket.priority_name) && (
                    <span className="px-2 py-1 rounded-full bg-red-100 text-red-800 flex items-center">
                      <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {ticket.priority || ticket.priority_name}
                    </span>
                  )}
                  
                  {(ticket.created_at || ticket.created) && (
                    <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-800 flex items-center">
                      <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {new Date(ticket.created_at || ticket.created).toLocaleDateString()}
                    </span>
                  )}
                </div>
                
                <div className="mt-3 text-sm text-gray-500">
                  <p>This Jira ticket was created to track issues with this test case. Click "View in Jira" to see full details.</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default JiraTicketsPanel; 